/**
 * Recoverable checkouts (card 3e5c00e6): `/api/v1/recovery/checkouts`, its
 * `/summary` and its `/export` CSV. `analytics.read` to read, `exports.create`
 * for the CSV. Production unless environments are named, like every read.
 *
 * Failed and cancelled orders, one line per competition, each with the one reason
 * a recovery email could or could not reach it. Nothing here sends anything.
 */

import { withEnvironments } from '@/lib/api'
import type { Environment } from './types'

export const RECOVERY_REASONS = [
  'paid_since',
  'competition_closed',
  'competition_unknown',
  'guest',
  'suppressed',
  'no_email_consent',
  'eligible',
] as const

export type RecoveryReason = (typeof RECOVERY_REASONS)[number]
export type IneligibleReason = Exclude<RecoveryReason, 'eligible'>

export const INELIGIBLE_REASONS = RECOVERY_REASONS.filter(
  (reason): reason is IneligibleReason => reason !== 'eligible',
)

/** What each reason means, in the words the page shows. Declaration order is precedence. */
export const REASON_LABELS: Record<RecoveryReason, string> = {
  paid_since: 'Paid since',
  competition_closed: 'Competition closed',
  competition_unknown: 'Competition not synced',
  guest: 'Guest checkout',
  suppressed: 'Unsubscribed or suppressed',
  no_email_consent: 'No email consent',
  eligible: 'Recoverable',
}

export interface RecoveryQuery {
  weeks: number
  environments: readonly Environment[]
  reason?: RecoveryReason
  limit?: number
  offset?: number
}

export interface RecoveryTally {
  count: number
  valueMinor: number
  /** Counted in `count`, but in another currency, so not in `valueMinor`. */
  otherCurrencyCount: number
}

export interface RecoveryWeek {
  weekStart: string
  eligible: RecoveryTally
  ineligible: RecoveryTally
  byReason: Record<IneligibleReason, RecoveryTally>
}

export interface RecoveryScope {
  environments: Environment[]
  nonProduction: boolean
  currency: string
  timezone: string
  minAgeMinutes: number
  generatedAt: string
  /** The window held more lines than one request reads; the oldest may be missing. */
  truncated: boolean
  /** When `truncated`: the oldest failure included. Figures before it are incomplete. */
  completeFrom: string | null
}

export interface RecoverableCheckoutsSummary extends RecoveryScope {
  weeks: RecoveryWeek[]
  totals: RecoveryWeek
}

export interface RecoverableCheckoutLine {
  orderId: string
  externalOrderId: string
  websiteId: string
  environment: Environment
  status: string
  failedAt: string
  weekStart: string
  customerId: string | null
  externalCustomerId: string | null
  competitionId: string | null
  externalCompetitionId: string | null
  competitionName: string | null
  quantity: number
  valueMinor: number
  currency: string
  reason: RecoveryReason
}

export interface RecoverableCheckoutsList extends RecoveryScope {
  total: number
  limit: number
  offset: number
  rows: RecoverableCheckoutLine[]
}

export interface RecoveryApi {
  recoverableCheckoutsSummary(query: RecoveryQuery): Promise<RecoverableCheckoutsSummary>
  recoverableCheckouts(query: RecoveryQuery): Promise<RecoverableCheckoutsList>
  /** The same lines as a CSV file (`exports.create`); a refusal rejects with the API's error. */
  recoverableCheckoutsCsv(query: RecoveryQuery): Promise<Blob>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

interface TallyOut {
  count: number
  value_minor: number
  other_currency_count: number
}

interface WeekOut {
  week_start: string
  eligible: TallyOut
  ineligible: TallyOut
  by_reason: Record<IneligibleReason, TallyOut>
}

interface ScopeOut {
  environments: Environment[]
  non_production: boolean
  currency: string
  timezone: string
  min_age_minutes: number
  generated_at: string
  truncated: boolean
  complete_from: string | null
}

export interface RecoverableCheckoutsSummaryOut extends ScopeOut {
  weeks: WeekOut[]
  totals: WeekOut
}

interface LineOut {
  order_id: string
  external_order_id: string
  website_id: string
  environment: Environment
  status: string
  failed_at: string
  week_start: string
  customer_id: string | null
  external_customer_id: string | null
  competition_id: string | null
  external_competition_id: string | null
  competition_name: string | null
  quantity: number
  value_minor: number
  currency: string
  reason: RecoveryReason
}

export interface RecoverableCheckoutsListOut extends ScopeOut {
  total: number
  limit: number
  offset: number
  rows: LineOut[]
}

function tallyFromWire(body: TallyOut): RecoveryTally {
  return { count: body.count, valueMinor: body.value_minor, otherCurrencyCount: body.other_currency_count }
}

function weekFromWire(body: WeekOut): RecoveryWeek {
  const byReason = {} as Record<IneligibleReason, RecoveryTally>
  for (const reason of INELIGIBLE_REASONS) {
    const tally = body.by_reason[reason]
    byReason[reason] = tally ? tallyFromWire(tally) : { count: 0, valueMinor: 0, otherCurrencyCount: 0 }
  }
  return {
    weekStart: body.week_start,
    eligible: tallyFromWire(body.eligible),
    ineligible: tallyFromWire(body.ineligible),
    byReason,
  }
}

function scopeFromWire(body: ScopeOut): RecoveryScope {
  return {
    environments: body.environments,
    nonProduction: body.non_production,
    currency: body.currency,
    timezone: body.timezone,
    minAgeMinutes: body.min_age_minutes,
    generatedAt: body.generated_at,
    truncated: body.truncated,
    completeFrom: body.complete_from,
  }
}

export function recoverySummaryFromWire(body: RecoverableCheckoutsSummaryOut): RecoverableCheckoutsSummary {
  return { ...scopeFromWire(body), weeks: body.weeks.map(weekFromWire), totals: weekFromWire(body.totals) }
}

export function recoveryListFromWire(body: RecoverableCheckoutsListOut): RecoverableCheckoutsList {
  return {
    ...scopeFromWire(body),
    total: body.total,
    limit: body.limit,
    offset: body.offset,
    rows: body.rows.map((row) => ({
      orderId: row.order_id,
      externalOrderId: row.external_order_id,
      websiteId: row.website_id,
      environment: row.environment,
      status: row.status,
      failedAt: row.failed_at,
      weekStart: row.week_start,
      customerId: row.customer_id,
      externalCustomerId: row.external_customer_id,
      competitionId: row.competition_id,
      externalCompetitionId: row.external_competition_id,
      competitionName: row.competition_name,
      quantity: row.quantity,
      valueMinor: row.value_minor,
      currency: row.currency,
      reason: row.reason,
    })),
  }
}

/** The path both reads and the CSV share, so the download is the list on screen. */
export function recoveryPath(base: string, query: RecoveryQuery): string {
  const params = new URLSearchParams({ weeks: String(query.weeks) })
  if (query.reason) params.set('reason', query.reason)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  return withEnvironments(`${base}?${params.toString()}`, query.environments)
}
